import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { Select, Tooltip, message } from "antd";
import StatusBadge from "../../Reuseable/StatusBadge";
import { paginate } from "../../../utlits/Paginate.js";
import Pagination from "../../Reuseable/Pagination";
import DataParser from "../../../utlits/Dataparser";
import ProspectAmountServices from "../../../Services/ProspectAmountServices";
import secureLocalStorage from "react-secure-storage";
import TableSkeleton from "../../Reuseable/TableSkeleton";

message.config({ zIndex: 1000000001 });

const { Option } = Select;

const ProspectAmount = () => {
  const navigate = useNavigate();
  const [amounts, setAmounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15);
  const [userPermissions, setUserPermissions] = useState([]);
  const [itemToDelete, setItemToDelete] = useState(null);
  const companyId = DataParser.CompanyId();

  // Form States (Create)
  const [minAmount, setMinAmount] = useState(null);
  const [maxAmount, setMaxAmount] = useState(null);
  const [currency, setCurrency] = useState("PKR");

  // Form States (Edit)
  const [editId, setEditId] = useState(null);
  const [editMin, setEditMin] = useState(null);
  const [editMax, setEditMax] = useState(null);
  const [editCurrency, setEditCurrency] = useState("PKR");

  const fetchAmounts = () => {
    setLoading(true);
    ProspectAmountServices.getProspectAmounts(companyId)
      .then((res) => {
        if (res.Good) {
          setAmounts(res.data.prospectAmounts || []);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAmounts();
    setUserPermissions(DataParser.CompanyUserPermissions());
  }, [companyId]);

  const clearCreateState = () => {
    setMinAmount(null);
    setMaxAmount(null);
    setCurrency("PKR");
  };

  const clearEditState = () => {
    setEditId(null);
    setEditMin(null);
    setEditMax(null);
    setEditCurrency("PKR");
  };

  const handleCreate = () => {
    if (minAmount === null || maxAmount === null) {
      return message.error("Both min and max amounts are required!");
    }
    if (minAmount > maxAmount) {
      return message.error("Min amount cannot be greater than max amount!");
    }

    const payload = { company_id: companyId, min_amount: minAmount, max_amount: maxAmount, currency };

    ProspectAmountServices.createProspectAmount(payload)
      .then((res) => {
        if (res.Good) {
          message.success("Prospect range created successfully!");
          fetchAmounts();
          setMinAmount(null);
          setMaxAmount(null);
          document.getElementById("closeCreateProspectModal").click();
        } else {
          message.warning("Could not create range. Please try again!");
        }
      })
      .catch(() => {
        message.error("Failed to create range. Please try again!");
      });
  };

  const handleEdit = (e, item) => {
    e.preventDefault();
    setEditId(item.id);
    setEditMin(item.min_amount);
    setEditMax(item.max_amount);
    setEditCurrency(item.currency || "PKR");
  };

  const handleUpdate = () => {
    if (editMin === null || editMax === null) {
      return message.error("Both min and max amounts are required!");
    }
    if (editMin > editMax) {
      return message.error("Min amount cannot be greater than max amount!");
    }

    const payload = { min_amount: editMin, max_amount: editMax, currency: editCurrency };

    ProspectAmountServices.updateProspectAmount(editId, payload)
      .then((res) => {
        if (res.Good === true || res.data?.Good === true || res.success === true) {
          message.success("Prospect range updated successfully!");
          fetchAmounts();
          document.getElementById("closeEditProspectModal").click();
          clearEditState();
        } else {
          fetchAmounts();
          document.getElementById("closeEditProspectModal").click();
        }
      })
      .catch(() => {
        message.error("Failed to update range. Please try again!");
      });
  };

  const handleDelete = (e, item) => {
    e.preventDefault();
    setItemToDelete(item);
    document.getElementById("openDeleteProspectModal").click();
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    const id = itemToDelete.id;
    setAmounts((prev) => prev.filter((el) => el.id !== id));
    document.getElementById("closeDeleteProspectModal").click();
    try {
      const res = await ProspectAmountServices.deleteProspectAmount(id);
      if (res.Good) {
        message.success("Prospect range deleted successfully!");
      } else {
        message.warning("Could not delete range.");
      }
      fetchAmounts();
    } catch {
      message.error("Failed to delete range. Please try again!");
      fetchAmounts();
    }
    setItemToDelete(null);
  };

  const paginatedData = paginate(amounts, currentPage, pageSize);

  return (
    <>
      <Helmet>
        <title>Prospect Amounts - Planit</title>
      </Helmet>
      <div className="main-content">
        <div className="page-content Pagebody" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12 px-0">
                <div className="d-flex align-items-end justify-content-end" style={{ padding: "0 0 12px 0" }}>
                  <div className="d-flex flex-column align-items-end" style={{ gap: "6px" }}>
                    <ol className="breadcrumb mb-0">
                      <li className="breadcrumb-item">
                        <Link to={`/${DataParser.CompanyIdentifierRole()}/employee-attendance`}>Home</Link>
                      </li>
                      <li className="breadcrumb-item active">
                        <Link to={`/${DataParser.CompanyIdentifierRole()}/settings`}>Settings</Link>
                      </li>
                      <li className="breadcrumb-item active">Prospect Amounts</li>
                    </ol>
                    {userPermissions.indexOf("prospectamount-create") !== -1 ? (
                      <button
                        type="button"
                        className="btn btn-primary waves-effect waves-light"
                        style={{ borderRadius: "5px" }}
                        data-bs-toggle="modal"
                        data-bs-target="#createProspectRangeModal"
                      >
                        Create Range
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary waves-effect waves-light"
                        disabled
                        style={{ opacity: 0.6, cursor: "not-allowed", borderRadius: "5px" }}
                      >
                        Create Range
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-12 px-0">
                <div className="card" style={{ backgroundColor: "#fff", boxShadow: "none", borderRadius: 0, border: "none" }}>
                  <div className="card-body p-0">
                    <div className="table-responsive listing-table-wrapper">
                      <table className="table mb-0 custom-table listing-table">
                        <thead>
                          <tr className="tableHeaderbg">
                            <th>Sr#</th>
                            <th>Range (Min - Max)</th>
                            <th>Currency</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <TableSkeleton cols={5} rows={6} />
                          ) : paginatedData.length > 0 ? (
                            paginatedData.map((item, index) => (
                              <tr key={item.id}>
                                <td>{index + 1}</td>
                                <td className="fw-bold">
                                  {item.min_amount.toLocaleString()} - {item.max_amount.toLocaleString()}
                                </td>
                                <td>
                                  <span className="badge-soft-primary px-2 py-1 rounded">{item.currency || "PKR"}</span>
                                </td>
                                <td>
                                  {userPermissions.indexOf("prospectamount-edit") !== -1 ? (
                                    <StatusBadge item={item} api={ProspectAmountServices.updateStatus} />
                                  ) : (
                                    <span className={`badge ${item.status === 1 ? "bg-success" : "bg-danger"} text-white`}>
                                      {item.status === 1 ? "Active" : "Inactive"}
                                    </span>
                                  )}
                                </td>
                                <td className="tableset">
                                  {userPermissions.indexOf("prospectamount-delete") !== -1 ? (
                                    <Link
                                      to="#"
                                      className="cursor-pointer iconscolor"
                                      onClick={(e) => handleDelete(e, item)}
                                    >
                                      <Tooltip title="Delete Range">
                                        <i className="uil-trash iconscolor" />
                                      </Tooltip>
                                    </Link>
                                  ) : (
                                    <span
                                      className="uil-trash iconscolor text-muted"
                                      style={{ cursor: "not-allowed", opacity: 0.5 }}
                                    />
                                  )}
                                  {userPermissions.indexOf("prospectamount-edit") !== -1 ? (
                                    <Link
                                      to="#"
                                      className="cursor-pointer iconscolor"
                                      onClick={(e) => handleEdit(e, item)}
                                      data-bs-toggle="modal"
                                      data-bs-target="#editProspectRangeModal"
                                    >
                                      <Tooltip title="Edit Range">
                                        <i className="uil-pen iconscolor" />
                                      </Tooltip>
                                    </Link>
                                  ) : (
                                    <span
                                      className="uil-pen iconscolor text-muted"
                                      style={{ cursor: "not-allowed", opacity: 0.5 }}
                                    />
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center">
                                Add your first prospect range by clicking the 'Create Range' button.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {amounts.length > pageSize && (
                        <div className="d-flex justify-content-center mt-4">
                          <Pagination
                            itemCount={amounts.length}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            onPageChange={(e, p) => { e.preventDefault(); setCurrentPage(p); }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hidden trigger for delete modal */}
        <div id="openDeleteProspectModal" data-bs-toggle="modal" data-bs-target="#deleteProspectModal" style={{ display: "none" }} />

        {/* Delete Confirmation Modal */}
        <div id="deleteProspectModal" className="modal fade" tabIndex={-1} role="dialog" aria-hidden="true" data-bs-keyboard="false">
          <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
            <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
              <div className="modal-header border-0 pb-1">
                <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Delete Prospect Range</h5>
                <button type="button" className="btn-close" id="closeDeleteProspectModal" data-bs-dismiss="modal" aria-label="Close" onClick={() => setItemToDelete(null)} />
              </div>
              <div className="modal-body pt-2 pb-2">
                {itemToDelete && (
                  <p style={{ fontSize: "14px", color: "#1D1D1D", marginBottom: "6px" }}>
                    Are you sure you want to delete the range{" "}
                    <strong>{itemToDelete.min_amount?.toLocaleString()} - {itemToDelete.max_amount?.toLocaleString()}</strong>?
                  </p>
                )}
                <p style={{ fontSize: "13px", color: "#ff4d4f", marginBottom: 0 }}>
                  <i className="uil uil-exclamation-triangle me-1" />
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer border-0 pt-1">
                <button type="button" className="btn btn-danger waves-effect waves-light" onClick={confirmDelete} style={{ borderRadius: "5px", fontSize: "13px" }}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Create Modal */}
        <div
          id="createProspectRangeModal"
          className="modal fade"
          tabIndex={-1}
          role="dialog"
          aria-hidden="true"
          data-bs-keyboard="false"
        >
          <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
            <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
              <div className="modal-header border-0 pb-1">
                <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Create Prospect Range</h5>
                <button
                  type="button"
                  className="btn-close"
                  id="closeCreateProspectModal"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={clearCreateState}
                />
              </div>
              <div className="modal-body pt-2 pb-2">
                <div className="float-input-wrap mb-3">
                  <input
                    type="number"
                    id="createMinAmount"
                    placeholder=" "
                    value={minAmount ?? ""}
                    onChange={(e) => setMinAmount(e.target.value === "" ? null : Number(e.target.value))}
                  />
                  <label htmlFor="createMinAmount">Min Amount*</label>
                </div>
                <div className="float-input-wrap mb-3">
                  <input
                    type="number"
                    id="createMaxAmount"
                    placeholder=" "
                    value={maxAmount ?? ""}
                    onChange={(e) => setMaxAmount(e.target.value === "" ? null : Number(e.target.value))}
                  />
                  <label htmlFor="createMaxAmount">Max Amount*</label>
                </div>
                <div className={`float-select-wrap ${currency ? "has-value" : ""}`}>
                  <label>Currency</label>
                  <Select placeholder="" value={currency} onChange={setCurrency}>
                    <Option value="PKR">PKR - Pakistani Rupee</Option>
                    <Option value="USD">USD - US Dollar</Option>
                    <Option value="AED">AED - UAE Dirham</Option>
                  </Select>
                </div>
              </div>
              <div className="modal-footer border-0 pt-1">
                <button
                  type="button"
                  onClick={handleCreate}
                  className="btn btn-primary waves-effect waves-light"
                  style={{ borderRadius: "5px", fontSize: "13px" }}
                >
                  + Create Range
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Modal */}
        <div
          id="editProspectRangeModal"
          className="modal fade"
          tabIndex={-1}
          role="dialog"
          aria-hidden="true"
          data-bs-keyboard="false"
        >
          <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
            <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
              <div className="modal-header border-0 pb-1">
                <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Edit Prospect Range</h5>
                <button
                  type="button"
                  className="btn-close"
                  id="closeEditProspectModal"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={clearEditState}
                />
              </div>
              <div className="modal-body pt-2 pb-2">
                <div className="float-input-wrap mb-3">
                  <input
                    type="number"
                    id="editMinAmount"
                    placeholder=" "
                    value={editMin ?? ""}
                    onChange={(e) => setEditMin(e.target.value === "" ? null : Number(e.target.value))}
                  />
                  <label htmlFor="editMinAmount">Min Amount*</label>
                </div>
                <div className="float-input-wrap mb-3">
                  <input
                    type="number"
                    id="editMaxAmount"
                    placeholder=" "
                    value={editMax ?? ""}
                    onChange={(e) => setEditMax(e.target.value === "" ? null : Number(e.target.value))}
                  />
                  <label htmlFor="editMaxAmount">Max Amount*</label>
                </div>
                <div className={`float-select-wrap ${editCurrency ? "has-value" : ""}`}>
                  <label>Currency</label>
                  <Select placeholder="" value={editCurrency} onChange={setEditCurrency}>
                    <Option value="PKR">PKR - Pakistani Rupee</Option>
                    <Option value="USD">USD - US Dollar</Option>
                    <Option value="AED">AED - UAE Dirham</Option>
                  </Select>
                </div>
              </div>
              <div className="modal-footer border-0 pt-1">
                <button
                  type="button"
                  onClick={handleUpdate}
                  className="btn btn-primary waves-effect waves-light"
                  style={{ borderRadius: "5px", fontSize: "13px" }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProspectAmount;
